import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Headphones, LoaderCircle, Pause, Play } from 'lucide-react';
import apiClient from '../services/apiClient';
import { isTtsAutoplayEnabled, setTtsAutoplayEnabled } from '../services/TtsPreferenceService';
import {
  claimSpeechPlayback,
  registerSpeechPlayback,
  releaseSpeechPlayback,
} from '../services/SpeechPlaybackCoordinator';
import './CardListeningControl.css';

let listeningControlSequence = 0;

function formatAudioTime(value) {
  const seconds = Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

function CardListeningControl({
  cardId,
  ttsText = null,
  isReading = false,
  isRevealed = false,
  isAnswerVisible = isRevealed,
  controlMode = 'auto',
  autoPlayOnAnswerVisible = false,
  autoplayEnabled: controlledAutoplayEnabled,
  showAutoplayToggle = true,
  visitToken = 0,
  canUseTts = true,
  onMembershipRequired,
  onAutoplayEnabledChange,
  onInteractionLockChange,
  onPlaybackCompleted,
  interactionDisabled = false,
}) {
  const [state, setState] = useState('loading');
  const [audioInfo, setAudioInfo] = useState(null);
  const [error, setError] = useState('');
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [localAutoplayEnabled, setAutoplayEnabled] = useState(() => isTtsAutoplayEnabled());
  const autoplayEnabled = controlledAutoplayEnabled ?? localAutoplayEnabled;
  const audioRef = useRef(null);
  const audioInfoRef = useRef(null);
  const objectUrlRef = useRef(null);
  const downloadControllerRef = useRef(null);
  const cycleRef = useRef({ active: false, completionReported: false, trigger: null, locksPractice: false });
  const pendingAutoplayRef = useRef(false);
  const autoplayConsumedRef = useRef(false);
  const autoplayCardIdRef = useRef(null);
  const previousAnswerVisibleRef = useRef(false);
  const mountedRef = useRef(true);
  const playbackOwnerRef = useRef(null);
  if (!playbackOwnerRef.current) {
    listeningControlSequence += 1;
    playbackOwnerRef.current = `generated-card-tts-${listeningControlSequence}`;
  }
  const onInteractionLockChangeRef = useRef(onInteractionLockChange);
  const onPlaybackCompletedRef = useRef(onPlaybackCompleted);

  useEffect(() => { onInteractionLockChangeRef.current = onInteractionLockChange; }, [onInteractionLockChange]);
  useEffect(() => { onPlaybackCompletedRef.current = onPlaybackCompleted; }, [onPlaybackCompleted]);

  const setPracticeLock = useCallback((locked) => {
    const normalized = Boolean(locked);
    if (cycleRef.current.locksPractice === normalized) return;
    cycleRef.current.locksPractice = normalized;
    onInteractionLockChangeRef.current?.(normalized);
  }, []);

  const finishCycle = useCallback(({ completed = false } = {}) => {
    const cycle = cycleRef.current;
    const shouldReport = completed && cycle.active && !cycle.completionReported && !isReading;
    const trigger = cycle.trigger;
    cycle.completionReported = cycle.completionReported || shouldReport;
    cycle.active = false;
    cycle.trigger = null;
    releaseSpeechPlayback(playbackOwnerRef.current);
    setPracticeLock(false);
    if (shouldReport) onPlaybackCompletedRef.current?.({ trigger });
  }, [isReading, setPracticeLock]);

  const releaseAudio = useCallback(({ unlock = true } = {}) => {
    downloadControllerRef.current?.abort();
    downloadControllerRef.current = null;
    const audioToRelease = audioRef.current;
    audioRef.current = null;
    if (audioToRelease) {
      audioToRelease.pause();
      audioToRelease.src = '';
    }
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    if (unlock) finishCycle({ completed: false });
    if (mountedRef.current) {
      setCurrentTime(0);
      setDuration(0);
    }
  }, [finishCycle]);

  useEffect(() => () => {
    mountedRef.current = false;
    pendingAutoplayRef.current = false;
    autoplayConsumedRef.current = false;
    previousAnswerVisibleRef.current = false;
    releaseAudio();
  }, [releaseAudio]);

  useEffect(() => registerSpeechPlayback(
    playbackOwnerRef.current,
    () => releaseAudio()
  ), [releaseAudio]);

  useEffect(() => {
    mountedRef.current = true;
    releaseAudio();
    pendingAutoplayRef.current = false;
    if (autoplayCardIdRef.current !== cardId) {
      autoplayCardIdRef.current = cardId;
      autoplayConsumedRef.current = false;
      previousAnswerVisibleRef.current = false;
    }
    audioInfoRef.current = null;
    setAudioInfo(null);
    setError('');
    if (!canUseTts) {
      setState('locked');
      return undefined;
    }
    if (!cardId) {
      setState('missing');
      return undefined;
    }

    let cancelled = false;
    setState('loading');
    Promise.resolve(apiClient.getCardTtsStatus?.(cardId) || { status: 'missing', audio: null })
      .then((result) => {
        if (cancelled) return;
        const nextAudio = result?.audio || null;
        audioInfoRef.current = nextAudio;
        setAudioInfo(nextAudio);
        if (result?.status === 'ready') setState('ready');
        else if (result?.status === 'unavailable') setState('unavailable');
        else setState('missing');
      })
      .catch((requestError) => {
        if (cancelled) return;
        setError(requestError.message || 'Listening status failed.');
        setState('error');
      });
    return () => { cancelled = true; };
  }, [canUseTts, cardId, ttsText, visitToken, releaseAudio]);

  const attachAudio = useCallback((objectUrl) => {
    const audio = new Audio(objectUrl);
    audio.preload = 'metadata';
    audio.addEventListener('loadedmetadata', () => {
      if (mountedRef.current && audioRef.current === audio) {
        setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
      }
    });
    audio.addEventListener('timeupdate', () => {
      if (mountedRef.current && audioRef.current === audio) {
        setCurrentTime(audio.currentTime || 0);
      }
    });
    audio.addEventListener('play', () => {
      if (!mountedRef.current || audioRef.current !== audio) return;
      setError('');
      setState('playing');
    });
    audio.addEventListener('pause', () => {
      if (mountedRef.current && audioRef.current === audio && !audio.ended) setState('ready');
    });
    audio.addEventListener('ended', () => {
      if (!mountedRef.current || audioRef.current !== audio) return;
      setCurrentTime(0);
      setState('ready');
      finishCycle({ completed: true });
    });
    audio.addEventListener('error', () => {
      // Clearing an old blob URL can emit `error` in Safari/iOS. Ignore events
      // from an element that has already been replaced by the next card.
      if (!mountedRef.current || audioRef.current !== audio) return;
      setError('Audio playback failed.');
      setState('error');
      finishCycle({ completed: false });
    });
    audioRef.current = audio;
    return audio;
  }, [finishCycle]);

  const ensureAudio = useCallback(async (info = audioInfoRef.current) => {
    if (audioRef.current) return audioRef.current;
    if (!info?.url) throw new Error('Listening audio is unavailable.');

    const controller = new AbortController();
    downloadControllerRef.current = controller;
    const blob = await apiClient.fetchTtsAudioBlob(info.url, { signal: controller.signal });
    if (!mountedRef.current || controller.signal.aborted) {
      throw Object.assign(new Error('Audio download cancelled'), { name: 'AbortError' });
    }
    const objectUrl = URL.createObjectURL(blob);
    objectUrlRef.current = objectUrl;
    downloadControllerRef.current = null;
    return attachAudio(objectUrl);
  }, [attachAudio]);

  const generateAudio = useCallback(async () => {
    setState('generating');
    const result = await apiClient.generateCardTts(cardId);
    const nextAudio = result?.audio || null;
    if (!nextAudio) throw new Error('Listening audio is unavailable.');
    audioInfoRef.current = nextAudio;
    setAudioInfo(nextAudio);
    setState('ready');
    return nextAudio;
  }, [cardId]);

  const startPlayback = useCallback(async ({ trigger = 'manual', locksPractice = !isReading } = {}) => {
    if (!canUseTts) {
      onMembershipRequired?.();
      return;
    }
    if (state === 'loading' || state === 'generating' || state === 'playing') return;
    if (state === 'unavailable') {
      setError('Listening needs a supported answer or an English question.');
      return;
    }

    claimSpeechPlayback(playbackOwnerRef.current);

    const isResuming = cycleRef.current.active;
    if (!isResuming) {
      cycleRef.current = { active: true, completionReported: false, trigger, locksPractice: false };
      if (locksPractice) setPracticeLock(true);
    }

    try {
      setError('');
      let info = audioInfoRef.current;
      if (!info || state === 'missing' || state === 'error') {
        releaseAudio({ unlock: false });
        info = await generateAudio();
      }
      const audio = await ensureAudio(info);
      if (audio.ended) audio.currentTime = 0;
      await audio.play();
    } catch (playError) {
      if (playError.name === 'AbortError') {
        finishCycle({ completed: false });
        return;
      }
      setState(audioInfoRef.current ? 'ready' : 'error');
      setError(trigger === 'auto' ? 'Tap Listening to play.' : (playError.message || 'Listening failed.'));
      finishCycle({ completed: false });
    }
  }, [canUseTts, ensureAudio, finishCycle, generateAudio, isReading, onMembershipRequired, releaseAudio, setPracticeLock, state]);

  useEffect(() => {
    const becameVisible = isAnswerVisible && !previousAnswerVisibleRef.current;
    previousAnswerVisibleRef.current = isAnswerVisible;
    if (!isAnswerVisible || controlledAutoplayEnabled === false) {
      pendingAutoplayRef.current = false;
      autoplayConsumedRef.current = false;
      return;
    }
    if (
      !isReading &&
      becameVisible &&
      (autoplayEnabled || autoPlayOnAnswerVisible) &&
      canUseTts &&
      !autoplayConsumedRef.current
    ) {
      autoplayConsumedRef.current = true;
      pendingAutoplayRef.current = true;
    }
  }, [autoPlayOnAnswerVisible, autoplayEnabled, controlledAutoplayEnabled, canUseTts, isAnswerVisible, isReading, visitToken]);

  useEffect(() => {
    if (!pendingAutoplayRef.current || state === 'loading' || state === 'generating') return;
    pendingAutoplayRef.current = false;
    void startPlayback({ trigger: 'auto', locksPractice: true });
  }, [isAnswerVisible, startPlayback, state, visitToken]);

  useEffect(() => {
    if (!isReading || state !== 'ready' || !audioInfo || audioRef.current) return;
    void ensureAudio(audioInfo).catch((loadError) => {
      if (loadError.name !== 'AbortError') setError('Audio download failed. Tap to retry.');
    });
  }, [audioInfo, ensureAudio, isReading, state]);

  const handleClick = async (event) => {
    event.stopPropagation();
    if (interactionDisabled) return;

    if (!canUseTts) {
      onMembershipRequired?.();
      return;
    }

    const isQuestionSideToggle = controlMode === 'toggle' || (controlMode === 'auto' && !isReading && !isAnswerVisible);
    if (isQuestionSideToggle) {
      const nextEnabled = setTtsAutoplayEnabled(!autoplayEnabled);
      setAutoplayEnabled(nextEnabled);
      onAutoplayEnabledChange?.(nextEnabled);
      return;
    }

    if (state === 'loading' || state === 'generating') return;
    if (state === 'playing') {
      audioRef.current?.pause();
      return;
    }
    await startPlayback({ trigger: 'manual', locksPractice: !isReading });
  };

  const isQuestionSideToggle = controlMode === 'toggle' || (controlMode === 'auto' && !isReading && !isAnswerVisible);
  const disabled = state === 'loading' || state === 'generating' || state === 'unavailable';
  const title = !canUseTts
    ? 'Membership listening'
    : isQuestionSideToggle
      ? (autoplayEnabled ? 'Turn automatic listening off' : 'Turn automatic listening on')
      : state === 'loading'
        ? 'Loading listening'
        : state === 'generating'
          ? 'Preparing listening'
      : state === 'unavailable'
        ? 'Listening unavailable'
        : state === 'playing'
          ? 'Pause listening'
          : 'Play listening';

  const seek = (event) => {
    const value = Number(event.target.value);
    if (!audioRef.current || !Number.isFinite(value)) return;
    audioRef.current.currentTime = value;
    setCurrentTime(value);
  };

  return (
    <div className={[
      'card-listening-control',
      `is-${state}`,
      isReading ? 'is-reading' : '',
      isQuestionSideToggle ? 'is-question-side' : 'is-answer-side',
      isQuestionSideToggle && autoplayEnabled ? 'is-autoplay-on' : '',
      isQuestionSideToggle && !autoplayEnabled ? 'is-autoplay-off' : '',
      cycleRef.current.active ? 'is-cycle-active' : '',
    ].filter(Boolean).join(' ')}>
      {(!isQuestionSideToggle || showAutoplayToggle) && <button
        type="button"
        className="card-listening-button"
        onClick={handleClick}
        disabled={interactionDisabled || (canUseTts && disabled && !isQuestionSideToggle)}
        aria-label={title}
        title={title}
        aria-pressed={isQuestionSideToggle ? autoplayEnabled : undefined}
      >
        {isQuestionSideToggle ? (
          <Headphones size={15} aria-hidden="true" />
        ) : state === 'loading' || state === 'generating' ? (
          <LoaderCircle className="card-listening-spinner" size={15} aria-hidden="true" />
        ) : state === 'playing' ? (
          <Pause size={15} aria-hidden="true" />
        ) : (
          <Play size={15} aria-hidden="true" />
        )}
      </button>}

      {isReading && audioInfo && (state === 'ready' || state === 'playing') && (
        <div className="card-listening-progress" aria-label="Listening progress">
          <span>{formatAudioTime(currentTime)}</span>
          <input
            type="range"
            min="0"
            max={Math.max(duration, 0)}
            step="0.1"
            value={Math.min(currentTime, duration || 0)}
            onChange={seek}
            disabled={interactionDisabled || !duration}
            aria-label="Seek listening audio"
          />
          <span>{formatAudioTime(duration)}</span>
        </div>
      )}
      {(error || state === 'unavailable') && (isReading || isAnswerVisible) && (
        <span className="card-listening-error" role="status">
          {error || 'Listening needs a supported answer or an English question.'}
        </span>
      )}
    </div>
  );
}

export { formatAudioTime };
export default CardListeningControl;
